import { Client } from '@microsoft/microsoft-graph-client';
import { OpportunityItem, UpdateTechnicalReviewPayload } from '../models/opportunity';
import { NovaOportunidadeForm } from '../components/NovaOportunidadeModal';

export class GraphService {
  private client: Client;
  private siteId: string;
  private listId: string;
  private powerAutomateUrl: string;

  constructor(client: Client, siteId: string, listId: string, powerAutomateUrl?: string) {
    this.client = client;
    this.siteId = siteId;
    this.listId = listId;
    this.powerAutomateUrl = powerAutomateUrl || '';
  }

  // Migrate legacy status values to new N1-N11 schema
  private migrateStatus(raw: string): string {
    const map: Record<string, string> = {
      '1. Levantamento Inicial':         '1. Levantamento Inicial',
      '2. Viabilidade Técnica Pendente': '2. Levantamento de Necessidades',
      '3. Proposta em Elaboração':       '4. Proposta em Elaboração',
      '4. Proposta Enviada':             '5. Proposta Enviada',
      '5. Ganho / Aceite':               '10. Concluído / Ganho',
      '6. Perdido / Cancelado':          '11. Perdido / Cancelado',
    };
    return map[raw] || raw;
  }

  async getOpportunities(): Promise<OpportunityItem[]> {
    const response = await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items`)
      .expand('fields')
      .get();

    return response.value.map((item: any) => {
      const f = item.fields;
      const rawStatus = f.StatusOportunidade || '1. Levantamento Inicial';
      const status = this.migrateStatus(rawStatus);
      // Only show progresso if status is N8 or above and value > 0
      const execStatuses = ['8. Em Execução', '9. Entrega & Validação', '10. Concluído / Ganho'];
      const progresso = execStatuses.includes(status) && f.ProgressoExecucao != null
        ? Number(f.ProgressoExecucao)
        : undefined;
      return {
        id: item.id,
        codigoOportunidade: f.Title || '',
        cliente: f.Cliente || '',
        contactoCliente: f.ContactoCliente || '',
        comercialEmail: f.ComercialEmail || '',
        comercialNome: f.ComercialNome || 'Comercial',
        statusOportunidade: status,
        ambitoNecessidades: f.AmbitoNecessidades || '',
        responsavelTecnicoEmail: f.TecnicoEmail || '',
        responsavelTecnicoNome: f.TecnicoNome || 'Técnico Não Atribuído',
        statusTecnico: f.StatusTecnico || 'Não Iniciado',
        parecerTecnico: f.ParecerTecnico || '',
        historicoParecer: f.HistoricoParecer || '',
        dataLimiteParecer: f.DataLimiteParecer ? f.DataLimiteParecer.substring(0, 10) : undefined,
        dataAceitacao: f.DataAceitacao ? f.DataAceitacao.substring(0, 10) : undefined,
        numeroOT: f.NumeroOT || '',
        progressoExecucao: progresso,
        diarioBordo: f.DiarioBordo || '',
        termoAceite: f.TermoAceite || '',
        motivoPerda: f.MotivoPerda || '',
      } as OpportunityItem;
    });
  }

  async getNextOpportunityCode(): Promise<string> {
    const year = new Date().getFullYear();
    const response = await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items`)
      .expand('fields($select=Title)')
      .get();
    const items: any[] = response.value || [];
    const prefix = `OPP-${year}-`;
    let max = 0;
    items.forEach((item: any) => {
      const title: string = item.fields?.Title || '';
      if (title.startsWith(prefix)) {
        const num = parseInt(title.replace(prefix, ''), 10);
        if (!isNaN(num) && num > max) max = num;
      }
    });
    return `${prefix}${String(max + 1).padStart(3, '0')}`;
  }

  async getTeamMembers(): Promise<{ nome: string; email: string }[]> {
    const TEAM_ID = '050ad99b-0592-40db-92a9-6cd66b0643ea';
    const response = await this.client.api(`/teams/${TEAM_ID}/members`).get();
    return (response.value || [])
      .map((m: any) => ({ nome: m.displayName || '', email: m.email || m.mail || '' }))
      .filter((m: any) => m.email)
      .sort((a: any, b: any) => a.nome.localeCompare(b.nome));
  }

  async createOpportunity(form: NovaOportunidadeForm): Promise<void> {
    await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items`)
      .post({
        fields: {
          Title: form.titulo,
          Cliente: form.cliente,
          ContactoCliente: form.contactoCliente,
          ComercialEmail: form.comercialEmail,
          ComercialNome: form.comercialNome,
          StatusOportunidade: form.statusOportunidade,
          AmbitoNecessidades: form.ambitoNecessidades,
          TecnicoEmail: form.tecnicoEmail,
          TecnicoNome: form.tecnicoNome,
          StatusTecnico: form.statusTecnico,
          DataLimiteParecer: form.dataLimiteParecer,
        },
      });
  }

  async updateOpportunityStatus(itemId: string, newStatus: string, extraFields?: Record<string, any>): Promise<void> {
    await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items/${itemId}/fields`)
      .update({ StatusOportunidade: newStatus, ...extraFields });
  }

  async updateTechnicalReview(
    itemId: string,
    payload: UpdateTechnicalReviewPayload,
    tecnicoNome: string,
    historicoActual: string,
    novoStatusOpp?: string
  ): Promise<void> {
    const now = new Date();
    const dataHora = `${now.toLocaleDateString('pt-PT')} ${now.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`;
    const novaEntrada = `[${dataHora}] ${tecnicoNome}:\n${payload.parecerTecnico}`;
    const novoHistorico = historicoActual
      ? `${novaEntrada}\n\n---\n\n${historicoActual}`
      : novaEntrada;

    const fieldsToUpdate: Record<string, any> = {
      StatusTecnico: payload.statusTecnico,
      ParecerTecnico: payload.parecerTecnico,
      HistoricoParecer: novoHistorico,
    };
    if (payload.novoStatusOportunidade) {
      fieldsToUpdate.StatusOportunidade = payload.novoStatusOportunidade;
    }
    if (novoStatusOpp) {
      fieldsToUpdate.StatusOportunidade = novoStatusOpp;
    }
    await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items/${itemId}/fields`)
      .update(fieldsToUpdate);
  }

  async updateExecution(itemId: string, progresso: number, entrada: string, diarioActual: string): Promise<void> {
    const now = new Date();
    const ts = `[${now.toLocaleDateString('pt-PT')} ${now.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}]`;
    const novoRegisto = `${ts}\n${entrada}`;
    const novoDiario = diarioActual ? `${novoRegisto}\n\n---\n\n${diarioActual}` : novoRegisto;
    await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items/${itemId}/fields`)
      .update({ ProgressoExecucao: progresso, DiarioBordo: novoDiario });
  }
}
